// Change #100463 - give one or more members a licence (Users > tick > Assign
// licences, or Assign licence on a row). A person can hold several licences,
// e.g. the Enterprise seat and a SIM, just not two of the same product, so
// the licence TYPE is picked here and anyone who already has it is skipped.
// All or nothing on the server: if there are not enough spare licences (or
// room on the Enterprise plan) for everyone who needs one, nobody is assigned.

import React, { useEffect, useMemo, useState } from "react";
import { Input } from "reactstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import AppModal from "../../Common/AppModal";
import LoadingSpinner from "../../Common/Spinner";
import EnterpriseSeats, { isEnterpriseBilling } from "../CreateEditCompanyUserLicenses/EnterpriseSeats";
import { isLivePaid, licenceState } from "./licenceState";
import {
  getLicensesSubscriptionListApi,
  subscriptionLicenceAssignBulkApi,
  subscriptionLicenceBillingApi,
} from "../../../helpers/fakebackend_helper";
import { FetchAPI } from "../../../helpers/APIdata/fetchAPI";

const SEAT = "seat";

/** Products a team-list row already holds (not ended). */
const heldProducts = (person) =>
  new Set(
    (person?.subscriptionlicence_list || [])
      .filter((l) =>
        isLivePaid({
          StartDateTime: l.SubscriptionLicence_StartDateTime,
          RenewalDateTime: l.SubscriptionLicence_RenewalDateTime,
          RenewalGraceDateTime: l.SubscriptionLicence_RenewalGraceDateTime,
          EndDateTime: l.SubscriptionLicence_EndDateTime,
        })
      )
      .map((l) => String(l.SubscriptionProduct_Id))
  );

// adminCompanyId (Change #100463): Back End > Company Details > Users, CRMit
// acting for another company through the developer-only admin routes.
export default function BulkAssignLicence({ open, people = [], onClose, onDone, adminCompanyId = null }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [billing, setBilling] = useState(null);
  const [licences, setLicences] = useState([]);
  const [choice, setChoice] = useState("");

  useEffect(() => {
    if (!open) return;
    setChoice("");
    setLoading(true);
    const billing = adminCompanyId
      ? FetchAPI(`/subscriptionlicence/admin/billing/${adminCompanyId}`, "GET")
      : subscriptionLicenceBillingApi();
    const list = adminCompanyId
      ? FetchAPI(`/subscriptionlicence/admin/list/company/active/${adminCompanyId}`, "GET")
      : getLicensesSubscriptionListApi();
    Promise.all([billing.catch(() => null), list.catch(() => null)])
      .then(([b, l]) => {
        setBilling(b?.status === 200 ? b.subscriptionbilling || null : null);
        setLicences(l?.subscriptionlicence_list || []);
      })
      .finally(() => setLoading(false));
  }, [open, adminCompanyId]);

  const enterprise = isEnterpriseBilling(billing) && billing?.PlanReady;
  const planId = enterprise ? String(billing.SubscriptionProduct_Id || "") : "";

  // Options: the Enterprise seat, then every product with spare licences
  // (paid up, or lapsed and waiting for its renewal to be paid).
  const options = useMemo(() => {
    const out = [];
    if (enterprise) {
      out.push({ key: SEAT, productId: planId, name: `${billing.PlanName || "Enterprise plan"} (user seat)`, spare: null });
    }
    const map = {};
    licences
      .filter(
        (l) =>
          !l?.User_Id &&
          ["active", "grace", "lapsed"].includes(licenceState(l)) &&
          String(l.SubscriptionProduct_Id) !== planId
      )
      .forEach((l) => {
        const k = String(l.SubscriptionProduct_Id);
        map[k] = map[k] || { key: k, productId: k, name: l.SubscriptionProduct_Name || `Product #${k}`, spare: 0, lapsed: 0 };
        map[k].spare += 1;
        if (licenceState(l) === "lapsed") map[k].lapsed += 1;
      });
    return out.concat(Object.values(map).sort((a, b) => a.name.localeCompare(b.name)));
  }, [licences, enterprise, planId, billing]);

  useEffect(() => {
    if (!choice && options.length) setChoice(options[0].key);
  }, [options, choice]);

  const selected = options.find((o) => o.key === choice) || null;
  const needs = useMemo(
    () => (selected ? people.filter((p) => !heldProducts(p).has(String(selected.productId))) : []),
    [people, selected]
  );
  const already = people.length - needs.length;
  const short = selected && selected.key !== SEAT && selected.spare < needs.length;

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const send = (body) =>
        adminCompanyId
          ? FetchAPI(`/subscriptionlicence/admin/assign/bulk/${adminCompanyId}`, "POST", body)
          : subscriptionLicenceAssignBulkApi(body);
      const res = await send([
        {
          User_Ids: needs.map((p) => Number(p.User_Id)),
          SubscriptionProduct_Id: Number(selected.productId),
        },
      ]);
      if (res?.status === 200) {
        toast.success(res.message || "Licences assigned");
        if (res.warning) toast.warning(res.warning);
        onDone && onDone();
      } else {
        toast.error(res?.message || "Could not assign the licences");
      }
    } catch (e) {
      toast.error("Could not assign the licences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={() => !saving && onClose()}
      title={people.length === 1 ? "Assign licence" : "Assign licences"}
      subtitle={
        people.length === 1
          ? people[0].User_UserFullName || people[0].User_Email
          : `${people.length} people`
      }
      icon="ri-key-2-line"
      headerAccent="indigo"
      size="sm"
      onSubmit={save}
      submitLabel={needs.length > 1 ? `Assign ${needs.length} licences` : "Assign licence"}
      submitDisabled={loading || saving || !selected || needs.length === 0 || short}
      loading={saving}
      loadingText="Assigning..."
    >
      {loading ? (
        <LoadingSpinner />
      ) : options.length === 0 ? (
        <div className="alert alert-warning mb-0 fs-13">
          You have no spare licences.{" "}
          <Link to="/settings/4/2" onClick={onClose} className="fw-semibold">
            Buy more in Manage Licence
          </Link>
          .
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {people.length > 1 && (
            <div className="border rounded-3 p-2" style={{ maxHeight: 140, overflowY: "auto" }}>
              {people.map((p) => {
                const has = selected && heldProducts(p).has(String(selected.productId));
                return (
                  <div key={p.User_Id} className={`fs-13 ${has ? "text-muted" : ""}`}>
                    <i className={`me-1 ${has ? "ri-checkbox-circle-line text-success" : "ri-user-line text-muted"}`}></i>
                    {p.User_UserFullName || p.User_Email}
                    {has ? " (already has it)" : ""}
                  </div>
                );
              })}
            </div>
          )}

          <div>
            <label className="form-label mb-1">Licence</label>
            <div className="d-flex flex-column gap-2">
              {options.map((o) => (
                <label
                  key={o.key}
                  className={`border rounded-3 p-2 d-flex align-items-center gap-2 mb-0 ${
                    o.key === choice ? "border-primary bg-primary-subtle" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                >
                  <Input
                    type="radio"
                    name="bulk-licence"
                    className="mt-0"
                    checked={o.key === choice}
                    onChange={() => setChoice(o.key)}
                  />
                  <div className="flex-grow-1 fw-semibold">{o.name}</div>
                  {o.spare != null && (
                    <span className={`badge ${o.lapsed === o.spare ? "badge-soft-danger" : "bg-light text-body"}`}>
                      {o.spare} spare{o.lapsed ? ` (${o.lapsed === o.spare ? "" : o.lapsed + " "}lapsed)` : ""}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>

          {selected?.key === SEAT ? (
            <EnterpriseSeats billing={billing} adding={needs.length} />
          ) : (
            selected && (
              <div className={`alert ${short ? "alert-warning" : "alert-info"} mb-0 fs-13`}>
                {selected.spare} spare for {needs.length} {needs.length === 1 ? "person" : "people"}.
                {/* Paid-up spares are used first; warn when lapsed ones will be needed. */}
                {selected.lapsed > 0 && needs.length > selected.spare - selected.lapsed && (
                  <div className="mt-1">
                    <i className="ri-error-warning-line me-1 text-danger"></i>
                    {selected.lapsed === 1 ? "One spare has" : `${selected.lapsed} spares have`} lapsed: its renewal has not
                    been paid. It can be assigned, but gives no access until the renewal is paid.
                  </div>
                )}
                {short && (
                  <>
                    {" "}
                    Buy {needs.length - selected.spare} more in{" "}
                    <Link to="/settings/4/2" onClick={onClose} className="fw-semibold">
                      Manage Licence
                    </Link>
                    , or pick fewer people.
                  </>
                )}
              </div>
            )
          )}
          {already > 0 && needs.length > 0 && (
            <div className="text-muted fs-12">
              {already} already {already === 1 ? "has" : "have"} this licence and will be skipped.
            </div>
          )}
          {selected && needs.length === 0 && (
            <div className="alert alert-light mb-0 fs-13">
              {people.length === 1 ? "They already have" : "Everyone picked already has"} this licence.
            </div>
          )}
        </div>
      )}
    </AppModal>
  );
}
